import { http } from '@/api/http';

/** Mirrors `libs/commons-lib/.../dto/system/response/LoggerLevelDTO.java`. */
export interface LoggerLevelDto {
  readonly configured_level: string | null;
  readonly effective_level: string | null;
}

/** Mirrors `libs/commons-lib/.../dto/system/response/InstanceLoggersEntryDTO.java`. */
export interface SystemLoggersInstanceDto {
  readonly instance_id: string | null;
  readonly host: string | null;
  readonly port: number;
  readonly status: string | null;
  readonly reachable: boolean;
  readonly error?: string;
  readonly available_levels: readonly string[];
  readonly loggers: Readonly<Record<string, LoggerLevelDto>>;
}

/** Mirrors `libs/commons-lib/.../dto/system/response/ServiceLoggersEntryDTO.java`. */
export interface SystemLoggersServiceDto {
  readonly service_id: string;
  readonly instances: readonly SystemLoggersInstanceDto[];
}

/** Mirrors `libs/commons-lib/.../dto/system/response/SystemLoggersDTO.java`. */
export interface SystemLoggersDto {
  readonly services: readonly SystemLoggersServiceDto[];
  readonly aggregated_at: string;
}

/**
 * Mirrors `libs/commons-lib/.../dto/system/request/SetLoggerLevelRequest.java`.
 * `level: null` (or omitted) clears the configured level so the logger inherits its
 * parent — same contract as Spring Boot Actuator's `/loggers/{name}` POST.
 */
export interface SetLoggerLevelRequestDto {
  readonly level: string | null;
}

export const adminLoggersEndpoints = {
  /**
   * GET /system/loggers — admin-only per-service, per-instance logger snapshot.
   *
   * Same admin gating as `/system/build-info`. The aggregator fans out to each service's
   * internal management actuator on port 9090 (not published in docker-compose, so it's
   * unauthenticated on the docker network); per-instance HTTP failures surface as
   * `reachable=false` instead of failing the whole call. Mirrors
   * `libs/commons-lib/.../dto/system/response/SystemLoggersDTO.java`.
   */
  getSystemLoggers: (options?: { signal?: AbortSignal }): Promise<SystemLoggersDto> =>
    http.get<SystemLoggersDto>('/system/loggers', options),

  /**
   * POST /system/loggers/{serviceId}/{instanceId}/{loggerName} — set one logger's level on
   * one instance. Pass `level: null` to clear the configured level (logger inherits parent).
   */
  setLoggerLevel: (
    serviceId: string,
    instanceId: string,
    loggerName: string,
    body: SetLoggerLevelRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.post<undefined>(
      `/system/loggers/${encodeURIComponent(serviceId)}/${encodeURIComponent(instanceId)}/${encodeURIComponent(loggerName)}`,
      body,
      { ...options, responseType: 'none' },
    ),

  /**
   * POST /system/loggers/{serviceId}/{loggerName} — fan out a logger level change to every
   * instance of a service. Best-effort: a per-instance failure aborts the rest.
   */
  setLoggerLevelOnService: (
    serviceId: string,
    loggerName: string,
    body: SetLoggerLevelRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.post<undefined>(
      `/system/loggers/${encodeURIComponent(serviceId)}/${encodeURIComponent(loggerName)}`,
      body,
      { ...options, responseType: 'none' },
    ),
};
